import { geoConfig } from '../data/geoConfig'

/**
 * Coarse geography and network signals for a page view.
 *
 * Deliberately NO IP handling in this app: the page never sees or stores an IP.
 * Sources are tried in priority order:
 *
 *   1. Meta tags injected by the edge when it serves the 503 page:
 *        <meta name="x-geo-country" content="IN">
 *        <meta name="x-geo-city" content="Bengaluru">
 *      Cloudflare Worker example:
 *        new HTMLRewriter().on('head', { element(head) {
 *          head.append(`<meta name="x-geo-country" content="${request.cf.country}">
 *                       <meta name="x-geo-city" content="${request.cf.city}">`,
 *                      { html: true })
 *        }}).transform(response)
 *   2. Cloudflare's /cdn-cgi/trace endpoint — country only, no setup.
 *   3. An optional third-party IP-geo API configured in data/geoConfig.ts, which
 *      also supplies the IP's time zone (and a VPN flag on paid plans).
 */
export interface VisitorGeo {
  country: string | null
  city: string | null
  /** IANA time zone reported for the IP, used for the VPN heuristic. */
  ipTimeZone: string | null
  /** True only when a provider explicitly flags a proxy/VPN. */
  vpnFlagged: boolean | null
  /** ASN organisation that owns the IP, e.g. 'M247 Ltd'. Identifies the network. */
  networkOrg: string | null
  /** AS number, e.g. 'AS9009'. */
  networkAsn: string | null
  /** IP truncated to its network block, e.g. '203.0.113.0/24'. Never a full address. */
  networkBlock: string | null
}

/**
 * Truncates an address to its network block: the final IPv4 octet and the lower
 * 80 bits of an IPv6 address are discarded before the value leaves this function,
 * so a single person can never be singled out from what is stored.
 */
export function toNetworkBlock(ip: string | null): string | null {
  if (!ip) return null

  const trimmed = ip.trim()
  const ipv4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.\d{1,3}$/.exec(trimmed)
  if (ipv4) return `${ipv4[1]}.${ipv4[2]}.${ipv4[3]}.0/24`

  if (trimmed.includes(':')) {
    const groups = trimmed.split(':').filter(Boolean).slice(0, 3)
    if (groups.length === 3) return `${groups.join(':')}::/48`
  }

  return null
}

const EMPTY: VisitorGeo = {
  country: null,
  city: null,
  ipTimeZone: null,
  vpnFlagged: null,
  networkOrg: null,
  networkAsn: null,
  networkBlock: null
}
// Version the key: a cached value written before network signals existed lacks
// ipTimeZone, and reusing it would permanently starve the VPN comparison.
const CACHE_KEY = 'turing.maintenance.geo.v2'

function readNested(source: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((current, key) => {
    if (current && typeof current === 'object') return (current as Record<string, unknown>)[key]
    return undefined
  }, source)
}

function fromMetaTags(): { country: string | null; city: string | null } {
  const read = (name: string) =>
    document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content?.trim() || null

  return { country: read('x-geo-country'), city: read('x-geo-city') }
}

function readCache(): VisitorGeo | null {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY)
    return raw ? ({ ...EMPTY, ...(JSON.parse(raw) as VisitorGeo) }) : null
  } catch {
    return null
  }
}

function writeCache(geo: VisitorGeo): void {
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(geo))
  } catch {
    /* cache is best-effort */
  }
}

async function countryFromCloudflareTrace(): Promise<string | null> {
  try {
    const response = await fetch('/cdn-cgi/trace', { cache: 'no-store' })
    if (!response.ok) return null
    const line = (await response.text()).split('\n').find(entry => entry.startsWith('loc='))
    const country = line?.slice(4).trim()
    return country && country !== 'XX' ? country : null
  } catch {
    return null
  }
}

async function fromProvider(): Promise<VisitorGeo> {
  if (!geoConfig.providerUrl.trim()) return EMPTY

  try {
    const response = await fetch(geoConfig.providerUrl, { cache: 'no-store' })
    if (!response.ok) return EMPTY

    const data = (await response.json()) as Record<string, unknown>
    const asString = (value: unknown) => (typeof value === 'string' && value ? value : null)
    const flag = geoConfig.vpnField ? readNested(data, geoConfig.vpnField) : undefined

    return {
      country: asString(data[geoConfig.countryField]),
      city: asString(data[geoConfig.cityField]),
      ipTimeZone: asString(data[geoConfig.timezoneField]),
      vpnFlagged: typeof flag === 'boolean' ? flag : null,
      networkOrg: asString(data[geoConfig.orgField]),
      networkAsn: asString(data[geoConfig.asnField]),
      // Truncated immediately; the full address is never retained.
      networkBlock: toNetworkBlock(asString(data[geoConfig.ipField]))
    }
  } catch {
    return EMPTY
  }
}

/** Minutes a time zone is offset from UTC at a given instant. */
function offsetMinutesFor(timeZone: string, at: Date): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset'
    }).formatToParts(at)

    const name = parts.find(part => part.type === 'timeZoneName')?.value ?? ''
    const match = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(name)
    if (!match) return name.includes('GMT') ? 0 : null

    const sign = match[1] === '-' ? -1 : 1
    return sign * (Number(match[2]) * 60 + Number(match[3] ?? 0))
  } catch {
    return null
  }
}

/**
 * How far the visitor's own clock sits from the clock at their IP's location, in
 * minutes. Zero means the browser and the IP agree; a large gap means the traffic
 * is being routed through another region — a far stronger VPN signal than simply
 * comparing time-zone names, which differ for many co-located zones.
 */
export function clockGapMinutes(geo: VisitorGeo): number | null {
  const local = browserTimeZone()
  if (!geo.ipTimeZone || !local) return null

  const now = new Date()
  const ipOffset = offsetMinutesFor(geo.ipTimeZone, now)
  const localOffset = offsetMinutesFor(local, now)
  if (ipOffset === null || localOffset === null) return null

  return localOffset - ipOffset
}

/** The visitor's own local hour, 0–23, for time-of-day correlation. */
export function localHour(): number {
  return new Date().getHours()
}

function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

/**
 * Whether this visitor is likely on a VPN or proxy.
 *
 * A provider flag wins when available. Otherwise it's a heuristic: the IP's time
 * zone disagreeing with the browser's own time zone. Treat it as directional at
 * the aggregate level, never as a verdict about an individual — travellers and
 * corporate networks trip it too.
 */
export function isVpnSuspected(geo: VisitorGeo): boolean | null {
  if (geo.vpnFlagged !== null) return geo.vpnFlagged

  const gap = clockGapMinutes(geo)
  if (gap === null) return null
  // Ignore sub-hour differences: co-located zones and DST edges produce those
  // without any tunnelling involved.
  return Math.abs(gap) >= 60
}

/** Resolves coarse geography from the cheapest available source. */
export async function resolveVisitorGeo(): Promise<VisitorGeo> {
  const meta = fromMetaTags()

  // Only trust the cache once it carries a network signal too, otherwise re-resolve.
  const cached = readCache()
  const cacheIsComplete =
    cached !== null &&
    (cached.country !== null || cached.city !== null) &&
    (cached.ipTimeZone !== null || cached.vpnFlagged !== null)

  if (cached && cacheIsComplete) {
    return {
      ...cached,
      country: meta.country ?? cached.country,
      city: meta.city ?? cached.city
    }
  }

  // The provider supplies the IP time zone that the VPN heuristic compares against,
  // so it is still worth calling even when the edge already gave us a city.
  const provider = await fromProvider()
  const traceCountry = meta.country ?? provider.country ?? (await countryFromCloudflareTrace())

  const resolved: VisitorGeo = {
    country: meta.country ?? provider.country ?? traceCountry,
    city: meta.city ?? provider.city,
    ipTimeZone: provider.ipTimeZone,
    vpnFlagged: provider.vpnFlagged,
    networkOrg: provider.networkOrg,
    networkAsn: provider.networkAsn,
    networkBlock: provider.networkBlock
  }

  if (resolved.country || resolved.city || resolved.ipTimeZone) writeCache(resolved)
  return resolved
}
